import { useEffect as useEffectM2, useMemo as useMemoM2, useRef as useRefM2, useState as useStateM2 } from "react";
import { Api } from "./api";
import { Banner, Field, Icon } from "./components";

// Community Marketplace — neighbor-to-neighbor sales board, scoped to
// the caller's active community. Browse, post, edit, mark sold.

const CATEGORY_LABEL = {
  furniture: "Furniture",
  appliances: "Appliances",
  home_garden: "Home & Garden",
  tools: "Tools",
  kids_baby: "Kids & Baby",
  sports_outdoors: "Sports & Outdoors",
  electronics: "Electronics",
  vehicles: "Vehicles",
  free: "Free / Curb",
  other: "Other",
};
const CATEGORY_ICON = {
  furniture: "home",
  appliances: "settings",
  home_garden: "spark",
  tools: "settings",
  kids_baby: "users",
  sports_outdoors: "trend",
  electronics: "spark",
  vehicles: "arrow",
  free: "tag",
  other: "tag",
};
const CONDITION_LABEL = {
  new: "New",
  like_new: "Like new",
  good: "Good",
  fair: "Fair",
  for_parts: "For parts",
};
const STATUS_PILL = {
  active: { text: "Available", cls: "pill--green" },
  pending: { text: "Pending sale", cls: "pill--gold" },
  sold: { text: "Sold", cls: "pill--ghost" },
  withdrawn: { text: "Withdrawn", cls: "pill--ghost" },
  expired: { text: "Expired", cls: "pill--ghost" },
};

function _fmtPriceM(listing) {
  if (listing.is_free) return "Free";
  if (listing.price_cents == null) return "Make offer";
  const dollars = listing.price_cents / 100;
  return "$" + dollars.toLocaleString(undefined, { minimumFractionDigits: dollars % 1 ? 2 : 0, maximumFractionDigits: 2 });
}

function _relTimeM(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const sec = Math.round(ms / 1000);
  if (sec < 60) return "just now";
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 7) return `${day}d ago`;
  const wk = Math.round(day / 7);
  if (wk < 5) return `${wk}w ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/* ─── AuthedImage: <img> for an authenticated API endpoint ─────────────── */
// CODE_REVIEW P2 L266: blob fetching is delegated to the shared
// `Api.authedBlobUrl` cache so concurrent renders / re-mounts of the
// same imageId reuse the existing object URL instead of re-hitting
// the droplet. Inbox polling used to refetch every thumbnail every
// 20s.
function AuthedImage({ imageId, alt = "", fallback = null, style }) {
  const [src, setSrc] = useStateM2(null);
  const [err, setErr] = useStateM2(false);
  useEffectM2(() => {
    if (!imageId) { setSrc(null); setErr(false); return; }
    let cancelled = false;
    let release = null;
    setErr(false);
    window.Api.authedBlobUrl(`/api/marketplace/images/${imageId}`).then(
      ({ url, release: r }) => {
        if (cancelled) { r(); return; }
        release = r;
        setSrc(url);
      },
      () => { if (!cancelled) setErr(true); },
    );
    return () => { cancelled = true; if (release) release(); };
  }, [imageId]);
  if (err) return fallback;
  if (!src) {
    return (
      <div style={{
        background: "var(--bg-2)",
        display: "flex", alignItems: "center", justifyContent: "center",
        color: "var(--mute)",
        ...style,
      }}>
        <Icon name="image" size={28} />
      </div>
    );
  }
  return <img src={src} alt={alt} style={style} />;
}

/* ─── Marketplace ─────────────────────────────────────────────────────── */
function Marketplace({ user, onMessageSeller }) {
  const [view, setView] = useStateM2("browse");  // browse | detail | new | edit
  const [activeListingId, setActiveListingId] = useStateM2(null);

  const open = (id) => { setActiveListingId(id); setView("detail"); };
  const openNew = () => { setActiveListingId(null); setView("new"); };
  const openEdit = (id) => { setActiveListingId(id); setView("edit"); };
  const back = () => { setView("browse"); setActiveListingId(null); };

  if (view === "detail" && activeListingId) {
    return (
      <ListingDetail
        id={activeListingId}
        user={user}
        onBack={back}
        onEdit={() => openEdit(activeListingId)}
        onDeleted={back}
        onMessageSeller={onMessageSeller}
      />
    );
  }
  if (view === "new") {
    return <ListingForm user={user} onCancel={back} onSaved={(l) => open(l.id)} />;
  }
  if (view === "edit" && activeListingId) {
    return <ListingForm user={user} editingId={activeListingId} onCancel={() => open(activeListingId)} onSaved={() => open(activeListingId)} />;
  }
  return <ListingBrowse user={user} onOpen={open} onNew={openNew} />;
}

/* ─── Browse ──────────────────────────────────────────────────────────── */
function ListingBrowse({ user, onOpen, onNew }) {
  const [state, setState] = useStateM2({ loading: true, error: null, listings: [], meta: null });
  const [usage, setUsage] = useStateM2(null);
  const [category, setCategory] = useStateM2("");
  const [mineOnly, setMineOnly] = useStateM2(false);
  const [hideSold, setHideSold] = useStateM2(true);
  const [q, setQ] = useStateM2("");
  // Offset for the {limit, offset, total, has_more} pagination
  // envelope (CODE_REVIEW P2 L268). Reset to 0 whenever any filter
  // changes — see the dedicated effect below.
  const [offset, setOffset] = useStateM2(0);
  // Request-invalidation counter (codex P1 on PR #156). The filter-
  // change path queues a `setOffset(0)` plus a `reload(offset)` in the
  // same render, so two requests fire — the old-offset one resolves
  // last and clobbers the fresh page. Each `reload` bumps this ref;
  // only the newest response wins.
  const reqIdRef = useRefM2(0);

  const reload = (nextOffset = offset) => {
    const myReq = ++reqIdRef.current;
    setState((s) => ({ loading: true, error: null, listings: [], meta: s.meta }));
    window.Api.marketplaceList({
      category: category || undefined,
      mine_only: mineOnly,
      status: hideSold ? "active,pending" : "active,pending,sold,withdrawn,expired",
      q: q || undefined,
      offset: nextOffset,
    })
      .then(({ items, meta }) => {
        if (myReq !== reqIdRef.current) return; // a newer reload superseded us
        setState({ loading: false, error: null, listings: items || [], meta: meta || null });
      })
      .catch((err) => {
        if (myReq !== reqIdRef.current) return;
        setState({ loading: false, error: err.message || "Couldn't load.", listings: [], meta: null });
      });
    // Usage isn't filter-dependent, so refresh it alongside on every reload.
    window.Api.marketplaceUsage().then(setUsage).catch(() => {});
  };
  // Reset to page 1 whenever a filter changes; reload will run via the
  // offset-effect below. Search debounce gets the same treatment via
  // the q effect.
  useEffectM2(() => { setOffset(0); /* eslint-disable-next-line */ }, [category, mineOnly, hideSold]);

  useEffectM2(() => { reload(offset); /* eslint-disable-next-line */ }, [category, mineOnly, hideSold, offset]);

  // q is search-as-you-type with a small debounce; resets to page 1.
  useEffectM2(() => {
    const t = setTimeout(() => { setOffset(0); reload(0); }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const community = user?.community?.name || "your community";

  return (
    <div className="scroll">
      <div className="stack-md">
        <Banner icon="tag" tone="accent">
          <b>{community} Marketplace.</b> Neighbors only — what you post here is visible only to active
          members of {community}. Be kind, be honest, and keep transactions between residents.
        </Banner>

        <div className="row" style={{ justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div className="card" style={{ padding: "8px 12px", flex: 1, display: "flex", alignItems: "center", gap: 10, minWidth: 260 }}>
            <Icon name="search" size={16} style={{ color: "var(--mute)" }} />
            <input
              className="input"
              aria-label="Search marketplace listings"
              placeholder="Search listings…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              style={{ border: 0, padding: 0, background: "transparent", width: "100%" }}
            />
          </div>
          <button
            className="btn btn--primary"
            onClick={onNew}
            disabled={usage && (usage.active >= usage.active_limit || usage.total >= usage.total_limit)}
            title={
              usage && usage.total >= usage.total_limit ? "You're at the max — mark one sold or delete one to free up a slot."
              : usage && usage.active >= usage.active_limit ? "Active-listing cap reached — mark one sold or withdrawn first."
              : undefined
            }
          >
            <Icon name="plus" size={16} /> &nbsp;Post an item
          </button>
        </div>

        {usage && <UsageBadge usage={usage} />}

        {/* Category chips */}
        <div className="row" style={{ flexWrap: "wrap", gap: 6 }}>
          <CategoryChip active={!category} onClick={() => setCategory("")} label="All" iconName="tag" />
          {Object.keys(CATEGORY_LABEL).map((k) => (
            <CategoryChip key={k} active={category === k} onClick={() => setCategory(k)} label={CATEGORY_LABEL[k]} iconName={CATEGORY_ICON[k]} />
          ))}
        </div>

        <div className="row" style={{ gap: 16, fontSize: 13, color: "var(--mute)" }}>
          <label className="row" style={{ gap: 6, cursor: "pointer" }}>
            <input type="checkbox" checked={mineOnly} onChange={(e) => setMineOnly(e.target.checked)} />
            Only my posts
          </label>
          <label className="row" style={{ gap: 6, cursor: "pointer" }}>
            <input type="checkbox" checked={hideSold} onChange={(e) => setHideSold(e.target.checked)} />
            Hide sold / withdrawn
          </label>
        </div>

        {state.loading && (
          <div className="card card--pad-lg" style={{ textAlign: "center", color: "var(--mute)" }}>
            Loading listings…
          </div>
        )}
        {state.error && (
          <div className="card card--pad-lg" style={{ color: "var(--danger)", textAlign: "center" }}>{state.error}</div>
        )}
        {!state.loading && !state.error && state.listings.length === 0 && (
          <div className="card card--pad-lg" style={{ textAlign: "center" }}>
            <div style={{ color: "var(--mute)", marginBottom: 14 }}>
              {q || category || mineOnly
                ? "No listings match your filters."
                : "No listings in your community yet. Be the first to post!"}
            </div>
            <button className="btn btn--primary" onClick={onNew}>Post the first item</button>
          </div>
        )}
        {!state.loading && state.listings.length > 0 && (
          <div className="grid" style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
            gap: 14,
          }}>
            {state.listings.map((l) => (
              <ListingCard key={l.id} listing={l} onClick={() => onOpen(l.id)} />
            ))}
          </div>
        )}
        <window.Pager meta={state.meta} offset={offset} onChange={setOffset} busy={state.loading} />
      </div>
    </div>
  );
}

function UsageBadge({ usage }) {
  const activePct = (usage.active / usage.active_limit) * 100;
  const totalPct = (usage.total / usage.total_limit) * 100;
  const nearActive = usage.active >= usage.active_limit;
  const nearTotal = usage.total >= usage.total_limit;
  const warn = nearActive || nearTotal || activePct >= 80 || totalPct >= 80;
  return (
    <div className="card" style={{
      padding: "10px 14px",
      display: "flex", flexWrap: "wrap", alignItems: "center", gap: 16,
      background: warn ? "var(--accent-soft, #eef3e6)" : "var(--surface)",
      border: warn ? "1px solid color-mix(in oklab, var(--accent, #7c8a6b) 22%, transparent)" : "1px solid var(--hairline)",
      fontSize: 13,
    }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 200, flex: 1 }}>
        <div className="row" style={{ justifyContent: "space-between" }}>
          <span style={{ fontWeight: 500 }}>Active listings</span>
          <span className="mono" style={{ color: nearActive ? "var(--danger)" : "var(--mute)" }}>
            {usage.active} / {usage.active_limit}
          </span>
        </div>
        <div style={{ height: 4, background: "var(--bg-2)", borderRadius: 2, overflow: "hidden" }}>
          <div style={{
            height: "100%",
            width: `${Math.min(100, activePct)}%`,
            background: nearActive ? "var(--danger)" : "var(--accent)",
          }} />
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 200, flex: 1 }}>
        <div className="row" style={{ justifyContent: "space-between" }}>
          <span style={{ fontWeight: 500 }}>Total in this community</span>
          <span className="mono" style={{ color: nearTotal ? "var(--danger)" : "var(--mute)" }}>
            {usage.total} / {usage.total_limit}
          </span>
        </div>
        <div style={{ height: 4, background: "var(--bg-2)", borderRadius: 2, overflow: "hidden" }}>
          <div style={{
            height: "100%",
            width: `${Math.min(100, totalPct)}%`,
            background: nearTotal ? "var(--danger)" : "var(--secondary, var(--accent))",
          }} />
        </div>
      </div>
      {warn && (
        <div style={{ fontSize: 12, color: "var(--mute)", flexBasis: "100%" }}>
          {nearTotal
            ? "You're at the max — mark one sold or delete one to free up a slot."
            : nearActive
            ? "Active cap reached — mark a current post sold, pending, or withdrawn to free a slot."
            : "Approaching your limit — listings auto-expire after 30 days if not sold."}
        </div>
      )}
    </div>
  );
}

function CategoryChip({ active, onClick, label, iconName }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: "6px 12px", borderRadius: 999,
        border: active ? "1px solid var(--accent)" : "1px solid var(--hairline)",
        background: active ? "var(--accent-soft)" : "var(--surface)",
        color: active ? "var(--accent-ink)" : "var(--ink)",
        fontSize: 13, fontWeight: active ? 500 : 400,
        cursor: "pointer",
      }}
    >
      <Icon name={iconName || "tag"} size={13} style={{ opacity: 0.7 }} />
      {label}
    </button>
  );
}

function ListingCard({ listing, onClick }) {
  const status = STATUS_PILL[listing.status] || STATUS_PILL.active;
  const firstImage = (listing.images || [])[0];
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "flex", flexDirection: "column",
        background: "var(--surface)",
        border: "1px solid var(--hairline)",
        borderRadius: 12,
        overflow: "hidden", cursor: "pointer",
        textAlign: "left", padding: 0,
        transition: "transform 80ms ease, box-shadow 80ms ease",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "var(--shadow-lg)"; e.currentTarget.style.transform = "translateY(-1px)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = ""; e.currentTarget.style.transform = ""; }}
    >
      <div style={{ aspectRatio: "4/3", position: "relative", background: "var(--bg-2)" }}>
        {firstImage ? (
          <AuthedImage
            imageId={firstImage.id}
            alt={listing.title}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        ) : (
          <div style={{ display: "flex", height: "100%", alignItems: "center", justifyContent: "center", color: "var(--mute)" }}>
            <Icon name="image" size={32} />
          </div>
        )}
        {listing.status !== "active" && (
          <span className={`pill ${status.cls}`} style={{ position: "absolute", top: 8, left: 8 }}>
            {status.text}
          </span>
        )}
        {listing.is_mine && (
          <span className="pill pill--accent" style={{ position: "absolute", top: 8, right: 8 }}>
            Your post
          </span>
        )}
      </div>
      <div style={{ padding: "10px 12px", display: "flex", flexDirection: "column", gap: 4 }}>
        <div style={{ fontWeight: 600, fontSize: 14, lineHeight: 1.3, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
          {listing.title}
        </div>
        <div className="row" style={{ justifyContent: "space-between", alignItems: "baseline" }}>
          <span style={{ fontWeight: 600, fontSize: 16, color: listing.is_free ? "var(--accent)" : "var(--ink)" }}>
            {_fmtPriceM(listing)}
          </span>
          <span className="text-xs text-mute">{_relTimeM(listing.created_at)}</span>
        </div>
        <div className="row" style={{ gap: 4, fontSize: 11, color: "var(--mute)" }}>
          <span>{CATEGORY_LABEL[listing.category] || listing.category}</span>
          <span>·</span>
          <span>{CONDITION_LABEL[listing.condition] || listing.condition}</span>
        </div>
      </div>
    </button>
  );
}

/* ─── Detail ──────────────────────────────────────────────────────────── */
function ListingDetail({ id, user, onBack, onEdit, onDeleted, onMessageSeller }) {
  const [state, setState] = useStateM2({ loading: true, error: null, listing: null });
  const [busy, setBusy] = useStateM2(false);
  const [activeImage, setActiveImage] = useStateM2(0);

  const reload = () => {
    setState({ loading: true, error: null, listing: null });
    window.Api.marketplaceGet(id)
      .then((listing) => setState({ loading: false, error: null, listing }))
      .catch((err) => setState({ loading: false, error: err.message || "Couldn't load.", listing: null }));
  };
  useEffectM2(() => { reload(); /* eslint-disable-next-line */ }, [id]);

  const setStatus = async (status) => {
    setBusy(true);
    try {
      const updated = await window.Api.marketplaceUpdate(id, { status });
      setState({ loading: false, error: null, listing: updated });
    } catch (err) {
      alert(err.message || "Couldn't update.");
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    if (!window.confirm("Delete this listing? This cannot be undone.")) return;
    setBusy(true);
    try {
      await window.Api.marketplaceDelete(id);
      onDeleted();
    } catch (err) {
      alert(err.message || "Couldn't delete.");
      setBusy(false);
    }
  };

  if (state.loading) return <div className="scroll"><div className="card card--pad-lg" style={{ textAlign: "center", color: "var(--mute)" }}>Loading…</div></div>;
  if (state.error || !state.listing) return <div className="scroll"><div className="card card--pad-lg" style={{ color: "var(--danger)", textAlign: "center" }}>{state.error || "Not found."}</div></div>;

  const l = state.listing;
  const statusPill = STATUS_PILL[l.status] || STATUS_PILL.active;
  const seller = l.seller;
  // Server provides display_name (derived from full_name or a non-PII
  // handle). Email is never used as a sellerName source because we
  // never render PII on the listing card.
  const sellerName = (
    (seller.display_name || "").trim()
    || (seller.full_name || "").trim()
    || "Neighbor"
  );

  return (
    <div className="scroll">
      <div className="stack-md" style={{ maxWidth: 980 }}>
        <div className="row" style={{ justifyContent: "space-between", alignItems: "center" }}>
          <button className="btn btn--ghost btn--sm" onClick={onBack}>
            <Icon name="chevron" size={14} style={{ transform: "rotate(180deg)" }} /> &nbsp;Back to marketplace
          </button>
          {l.can_edit && (
            <div className="row" style={{ gap: 8 }}>
              {/* Edit stays available even after a sale so a seller
                  can correct a typo on a sold listing while it's
                  still in their history. Delete obviously needs to
                  remain available so they can free a total-cap slot. */}
              <button className="btn btn--ghost btn--sm" onClick={onEdit} disabled={busy}>
                <Icon name="edit" size={14} /> &nbsp;Edit
              </button>
              <button className="btn btn--ghost btn--sm" onClick={onDelete} disabled={busy} style={{ color: "var(--danger)" }}>
                <Icon name="trash" size={14} /> &nbsp;Delete
              </button>
            </div>
          )}
        </div>

        <div className="grid" style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.4fr) minmax(280px, 1fr)", gap: 24 }}>
          {/* Images */}
          <div>
            <div style={{
              aspectRatio: "4/3",
              background: "var(--bg-2)",
              borderRadius: 12,
              overflow: "hidden",
              position: "relative",
            }}>
              {l.images.length > 0 ? (
                <AuthedImage
                  imageId={l.images[activeImage]?.id}
                  alt={l.title}
                  style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
                />
              ) : (
                <div style={{ display: "flex", height: "100%", alignItems: "center", justifyContent: "center", color: "var(--mute)" }}>
                  <Icon name="image" size={56} />
                </div>
              )}
              {l.status !== "active" && (
                <span className={`pill ${statusPill.cls}`} style={{ position: "absolute", top: 12, left: 12 }}>
                  {statusPill.text}
                </span>
              )}
            </div>
            {l.images.length > 1 && (
              <div className="row" style={{ gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                {l.images.map((img, i) => (
                  <button
                    key={img.id}
                    type="button"
                    onClick={() => setActiveImage(i)}
                    style={{
                      width: 64, height: 64, padding: 0,
                      borderRadius: 8, overflow: "hidden",
                      border: i === activeImage ? "2px solid var(--accent)" : "1px solid var(--hairline)",
                      cursor: "pointer", background: "var(--bg-2)",
                    }}
                  >
                    <AuthedImage imageId={img.id} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info column */}
          <div className="col" style={{ gap: 16 }}>
            <div>
              <div className="text-sm text-mute" style={{ marginBottom: 4 }}>
                {CATEGORY_LABEL[l.category] || l.category}
              </div>
              <h1 style={{ fontSize: 24, fontWeight: 600, margin: 0, lineHeight: 1.2 }}>{l.title}</h1>
              <div style={{ fontSize: 28, fontWeight: 600, marginTop: 8, color: l.is_free ? "var(--accent)" : "var(--ink)" }}>
                {_fmtPriceM(l)}
                {l.is_negotiable && !l.is_free && <span className="text-sm text-mute" style={{ fontWeight: 400, marginLeft: 6 }}>· OBO</span>}
              </div>
              <div className="row" style={{ gap: 6, marginTop: 6, fontSize: 13, color: "var(--mute)" }}>
                <span>{CONDITION_LABEL[l.condition] || l.condition}</span>
                <span>·</span>
                <span>Posted {_relTimeM(l.created_at)}</span>
              </div>
            </div>

            {l.description && (
              <div style={{ whiteSpace: "pre-wrap", fontSize: 14, lineHeight: 1.55 }}>
                {l.description}
              </div>
            )}

            <div className="card" style={{ padding: 14 }}>
              <div className="text-xs text-mute" style={{ marginBottom: 6, letterSpacing: 0.4, textTransform: "uppercase" }}>
                Seller
              </div>
              <div style={{ fontWeight: 600 }}>{sellerName}</div>
              {seller.email && (
                <div className="text-sm text-mute mono" style={{ wordBreak: "break-all", marginTop: 4 }}>
                  <span className="pill" style={{ marginRight: 6 }}>Moderator view</span>
                  {seller.email}
                </div>
              )}
              {!l.is_mine && l.status === "active" && (
                <button
                  type="button"
                  className="btn btn--primary btn--block"
                  style={{ marginTop: 10 }}
                  onClick={() => {
                    if (onMessageSeller) {
                      onMessageSeller({
                        recipient_user_id: seller.id,
                        recipient_name: sellerName,
                        related_listing_id: l.id,
                        listing_title: l.title,
                      });
                    }
                  }}
                  disabled={!onMessageSeller}
                >
                  Message {sellerName.split(" ")[0]}
                </button>
              )}
            </div>

            {/* Owner-only status actions */}
            {l.is_mine && l.status !== "sold" && (
              <div className="card" style={{ padding: 14 }}>
                <div className="text-xs text-mute" style={{ marginBottom: 6, letterSpacing: 0.4, textTransform: "uppercase" }}>
                  Status
                </div>
                <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
                  {l.status !== "active" && (
                    <button className="btn btn--ghost btn--sm" onClick={() => setStatus("active")} disabled={busy}>Re-list</button>
                  )}
                  {l.status === "active" && (
                    <button className="btn btn--ghost btn--sm" onClick={() => setStatus("pending")} disabled={busy}>Mark pending</button>
                  )}
                  <button className="btn btn--primary btn--sm" onClick={() => setStatus("sold")} disabled={busy}>
                    <Icon name="sold" size={14} />&nbsp; Mark as sold
                  </button>
                  {l.status === "active" && (
                    <button className="btn btn--ghost btn--sm" onClick={() => setStatus("withdrawn")} disabled={busy}>Withdraw</button>
                  )}
                </div>
              </div>
            )}

            {/* Moderation actions for managers who aren't the seller */}
            {!l.is_mine && l.can_moderate && (
              <div className="card" style={{ padding: 14 }}>
                <div className="text-xs text-mute" style={{ marginBottom: 6 }}>Moderator</div>
                <button className="btn btn--ghost btn--sm" onClick={onDelete} style={{ color: "var(--danger)" }} disabled={busy}>
                  Remove this listing
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Create / Edit form ─────────────────────────────────────────────── */
function ListingForm({ user, editingId, onCancel, onSaved }) {
  const isEdit = !!editingId;
  const [loaded, setLoaded] = useStateM2(!isEdit);
  const [title, setTitle] = useStateM2("");
  const [description, setDescription] = useStateM2("");
  const [category, setCategory] = useStateM2("other");
  const [condition, setCondition] = useStateM2("good");
  const [price, setPrice] = useStateM2("");
  const [isFree, setIsFree] = useStateM2(false);
  const [isNegotiable, setIsNegotiable] = useStateM2(true);
  const [images, setImages] = useStateM2([]);  // [{id, ordinal, content_type}]
  const [pendingFiles, setPendingFiles] = useStateM2([]);
  const [busy, setBusy] = useStateM2(false);
  const [error, setError] = useStateM2(null);
  const fileInputRef = useRefM2(null);

  // Revoke any blob URLs still attached to pendingFiles when the form
  // unmounts (route change / cancel). Per-item URLs are also revoked on
  // explicit remove via onRemovePending; this catches the rest.
  // Ref dance so the cleanup sees the latest pendingFiles without
  // re-running on every change (which would revoke URLs that are
  // still in use).
  const pendingFilesRef = useRefM2(pendingFiles);
  useEffectM2(() => {
    pendingFilesRef.current = pendingFiles;
  }, [pendingFiles]);
  useEffectM2(() => {
    return () => {
      pendingFilesRef.current.forEach((f) => {
        if (f?.previewUrl) URL.revokeObjectURL(f.previewUrl);
      });
    };
  }, []);

  useEffectM2(() => {
    if (!isEdit) return;
    window.Api.marketplaceGet(editingId).then((l) => {
      setTitle(l.title);
      setDescription(l.description || "");
      setCategory(l.category);
      setCondition(l.condition);
      setIsFree(l.is_free);
      setIsNegotiable(l.is_negotiable);
      setPrice(l.price_cents != null ? (l.price_cents / 100).toString() : "");
      setImages(l.images || []);
      setLoaded(true);
    }).catch((err) => setError(err.message || "Couldn't load."));
  }, [editingId, isEdit]);

  // Wrap each picked File with a stable id + cached blob URL so React
  // can key by id (not array index) and we revoke the URL exactly once
  // when the file is removed or the form unmounts. Inline
  // `URL.createObjectURL(f)` on every render allocated a new blob URL
  // per keystroke (monotonic memory leak) and the index-key caused the
  // wrong-photo-after-delete bug because React reused the kept-index
  // <img> node with a new src (codex P1 on #244).
  const onFilesPicked = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const room = 6 - images.length - pendingFiles.length;
    if (room <= 0) {
      setError("Max 6 images per listing.");
      return;
    }
    const wrapped = files.slice(0, room).map((file) => ({
      id: (window.crypto?.randomUUID?.()) || `tmp-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    setPendingFiles((cur) => [...cur, ...wrapped]);
    e.target.value = "";
  };

  const onRemovePending = (id) => {
    setPendingFiles((cur) => {
      const removed = cur.find((f) => f.id === id);
      if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
      return cur.filter((f) => f.id !== id);
    });
  };

  const onRemoveExisting = async (imageId) => {
    if (!window.confirm("Remove this image?")) return;
    try {
      await window.Api.marketplaceDeleteImage(imageId);
      setImages((cur) => cur.filter((i) => i.id !== imageId));
    } catch (err) {
      setError(err.message || "Couldn't remove image.");
    }
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (busy) return;
    setError(null);
    if (!title.trim()) { setError("Title is required."); return; }
    if (!isFree && price && isNaN(parseFloat(price.replace(/[$,]/g, "")))) {
      setError("Price must be a number, or check 'Free'."); return;
    }

    setBusy(true);
    try {
      const body = {
        title: title.trim(),
        description: description.trim() || undefined,
        category,
        condition,
        is_free: isFree,
        is_negotiable: isNegotiable,
        price: isFree ? null : (price.trim() || null),
      };
      const saved = isEdit
        ? await window.Api.marketplaceUpdate(editingId, body)
        : await window.Api.marketplaceCreate(body);
      // Upload any pending new files attached to the resulting id.
      // Track failures so we can tell the user something went wrong —
      // pre-#251 we only console.warn'd and silently fired onSaved
      // with a partial photo set (codex P2 on #251). Bandwidth-flaky
      // mobile networks dropped 1-3 photos with zero indication.
      const failedUploads = [];
      for (const wrapped of pendingFiles) {
        try {
          await window.Api.marketplaceUploadImage(saved.id, wrapped.file);
        } catch (uploadErr) {
          console.warn("image upload failed:", uploadErr);
          failedUploads.push(wrapped);
        }
      }
      if (failedUploads.length > 0) {
        // Listing itself saved — surface a non-blocking alert so the
        // user knows photos are missing and can re-edit to retry,
        // then close the form the same way the happy path does.
        window.alert(
          `Saved, but ${failedUploads.length} photo${failedUploads.length === 1 ? "" : "s"} couldn't upload (likely a flaky connection). Open the listing to add ${failedUploads.length === 1 ? "it" : "them"} again.`,
        );
      }
      onSaved(saved);
    } catch (err) {
      setError(err.message || "Couldn't save.");
    } finally {
      setBusy(false);
    }
  };

  if (isEdit && !loaded) {
    return <div className="scroll"><div className="card card--pad-lg" style={{ color: "var(--mute)", textAlign: "center" }}>Loading…</div></div>;
  }

  return (
    <div className="scroll">
      <form className="stack-md" onSubmit={submit} style={{ maxWidth: 700 }}>
        <div className="row" style={{ justifyContent: "space-between", alignItems: "center" }}>
          <h2 style={{ fontSize: 22, fontWeight: 600, margin: 0 }}>{isEdit ? "Edit listing" : "Post an item"}</h2>
          <button type="button" className="btn btn--ghost btn--sm" onClick={onCancel}>Cancel</button>
        </div>

        {error && (
          <div role="alert" style={{ padding: "10px 12px", borderRadius: 8, background: "var(--danger-soft, #fdecea)", color: "var(--danger, #b3261e)" }}>
            {error}
          </div>
        )}

        <div className="card card--pad-lg" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Field label="Title" hint="What is it? Keep it short and descriptive.">
            <input
              className="input" value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Sectional couch — leather, 8 yrs old"
              maxLength={140}
            />
          </Field>

          <Field label="Description" hint="Condition details, pickup info, dimensions, anything a buyer should know.">
            <textarea
              className="input"
              rows={5}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Selling because we're remodeling. Some wear on the left armrest, otherwise great shape. Pickup only — must be able to load yourself. Cash or Venmo."
              style={{ resize: "vertical", lineHeight: 1.5 }}
            />
          </Field>

          <div className="auth__form-row" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <Field label="Category">
              <select className="select" value={category} onChange={(e) => setCategory(e.target.value)}>
                {Object.entries(CATEGORY_LABEL).map(([k, l]) => (
                  <option key={k} value={k}>{l}</option>
                ))}
              </select>
            </Field>
            <Field label="Condition">
              <select className="select" value={condition} onChange={(e) => setCondition(e.target.value)}>
                {Object.entries(CONDITION_LABEL).map(([k, l]) => (
                  <option key={k} value={k}>{l}</option>
                ))}
              </select>
            </Field>
          </div>

          <Field label="Price" hint={isFree ? "Free — no price." : "USD. Leave blank for 'make offer'."}>
            <div className="row" style={{ gap: 10, alignItems: "center" }}>
              <input
                className="input"
                style={{ flex: 1 }}
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="e.g. 250"
                disabled={isFree}
              />
              <label className="row" style={{ gap: 6, fontSize: 13, cursor: "pointer", whiteSpace: "nowrap" }}>
                <input type="checkbox" checked={isFree} onChange={(e) => { setIsFree(e.target.checked); if (e.target.checked) setPrice(""); }} />
                Free / curb
              </label>
              <label className="row" style={{ gap: 6, fontSize: 13, cursor: "pointer", whiteSpace: "nowrap" }}>
                <input type="checkbox" checked={isNegotiable} onChange={(e) => setIsNegotiable(e.target.checked)} disabled={isFree} />
                Negotiable
              </label>
            </div>
          </Field>
        </div>

        {/* Images */}
        <div className="card card--pad-lg">
          <div className="row" style={{ justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontWeight: 500 }}>Photos</div>
              <div className="text-xs text-mute">Up to 6 images. First image is the cover.</div>
            </div>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={images.length + pendingFiles.length >= 6}
            >
              <Icon name="upload" size={14} />&nbsp; Add images
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={onFilesPicked}
            />
          </div>
          {(images.length > 0 || pendingFiles.length > 0) && (
            <div className="row" style={{ gap: 10, marginTop: 12, flexWrap: "wrap" }}>
              {images.map((img) => (
                <div key={img.id} style={{ position: "relative", width: 88, height: 88, borderRadius: 8, overflow: "hidden", border: "1px solid var(--hairline)" }}>
                  <AuthedImage imageId={img.id} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                  <button
                    type="button"
                    onClick={() => onRemoveExisting(img.id)}
                    title="Remove image"
                    aria-label="Remove image"
                    style={{ position: "absolute", top: 4, right: 4, width: 22, height: 22, borderRadius: "50%", background: "rgba(0,0,0,0.6)", color: "#fff", border: 0, fontSize: 12, cursor: "pointer" }}
                  >
                    ×
                  </button>
                </div>
              ))}
              {pendingFiles.map((f) => (
                <div key={f.id} style={{ position: "relative", width: 88, height: 88, borderRadius: 8, overflow: "hidden", border: "1px dashed var(--accent)" }}>
                  <img src={f.previewUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                  <button
                    type="button"
                    onClick={() => onRemovePending(f.id)}
                    title="Remove image"
                    aria-label="Remove pending image"
                    style={{ position: "absolute", top: 4, right: 4, width: 22, height: 22, borderRadius: "50%", background: "rgba(0,0,0,0.6)", color: "#fff", border: 0, fontSize: 12, cursor: "pointer" }}
                  >
                    ×
                  </button>
                  <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, background: "rgba(0,0,0,0.55)", color: "#fff", fontSize: 10, textAlign: "center", padding: "2px 0" }}>
                    will upload
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="row" style={{ justifyContent: "flex-end", gap: 8 }}>
          <button type="button" className="btn btn--ghost" onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="submit" className="btn btn--primary" disabled={busy}>
            {busy ? "Saving…" : (isEdit ? "Save changes" : "Post listing")}
          </button>
        </div>
      </form>
    </div>
  );
}

export { Marketplace };
